/** Routes and the auth gate. */

import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Workspace, api, session } from "./lib/api";
import { usePolling } from "./lib/useLive";
import { Shell, Splash } from "./components/ui";
import { Login, Overview, Processes, Runs } from "./pages/core";
import { ProcessBuilder } from "./pages/builder";
import { ProcessDetail, RunView } from "./pages/run";
import { Apps, Compute, Data, Events, HoloPods, Models, Settings, Sync } from "./pages/manage";
import "./styles/app.css";

export default function App() {
  const [ready, setReady] = useState(false);
  const [signedIn, setSignedIn] = useState(Boolean(session.token()));
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [active, setActive] = useState(session.workspace() ?? "");

  useEffect(() => {
    if (!session.token()) { setReady(true); return; }
    api.me()
      .then((result) => {
        setWorkspaces(result.workspaces);
        if (!session.workspace() && result.workspaces[0]) {
          session.set(session.token()!, result.workspaces[0].id);
          setActive(result.workspaces[0].id);
        }
        setSignedIn(true);
      })
      .catch(() => { session.clear(); setSignedIn(false); })
      .finally(() => setReady(true));
  }, [signedIn]);

  if (!ready) return <Splash />;

  if (!signedIn) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<Login onSignedIn={() => setSignedIn(true)} />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Frame workspaces={workspaces} active={active} onSwitch={(id) => {
        session.set(session.token()!, id);
        setActive(id);
        location.reload();
      }} />
    </BrowserRouter>
  );
}

function Frame({ workspaces, active, onSwitch }: {
  workspaces: Workspace[];
  active: string;
  onSwitch: (id: string) => void;
}) {
  // Polled here so the running count in the nav stays honest on every page.
  const { data } = usePolling(() => api.dashboard(), 8000);

  return (
    <Shell
      workspaces={workspaces}
      activeWorkspace={active}
      onWorkspaceChange={onSwitch}
      runningCount={data?.runs.running ?? 0}
    >
      <Routes>
        <Route path="/" element={<Overview />} />
        <Route path="/processes" element={<Processes />} />
        <Route path="/processes/new" element={<ProcessBuilder />} />
        <Route path="/processes/:id" element={<ProcessDetail />} />
        <Route path="/processes/:id/edit" element={<ProcessBuilder />} />
        <Route path="/runs" element={<Runs />} />
        <Route path="/runs/:id" element={<RunView />} />
        <Route path="/holopods" element={<HoloPods />} />
        <Route path="/apps" element={<Apps />} />
        <Route path="/models" element={<Models />} />
        <Route path="/sync" element={<Sync />} />
        <Route path="/data" element={<Data />} />
        <Route path="/compute" element={<Compute />} />
        <Route path="/events" element={<Events />} />
        <Route path="/settings" element={<Settings workspaces={workspaces} activeWorkspace={active} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
